import { waitForTmaInitData } from '@common/services/tmaInitDataService';
import { fetcher } from '@common/utils/fetcher';

import { type User } from './UsersService';

export const MATCHMAKER_ERROR_CODES = {
  featureDisabled: 'feature_disabled',
  notApproved: 'not_approved',
  notMatchmaker: 'not_matchmaker',
  notAvailable: 'not_available',
  swipeLimit: 'swipe_limit',
  browseLimit: 'browse_limit',
  alreadyRecommended: 'already_recommended',
  candidateUnavailable: 'candidate_unavailable',
  dailyCap: 'daily_cap',
  candidateCap: 'candidate_cap',
  invalidNote: 'invalid_note',
  invalidCursor: 'invalid_cursor',
  initDataUnverified: 'init_data_unverified',
} as const;

export type FriendSex = '0' | '1';

export type MatchmakerFriend = {
  sex: FriendSex;
  age: number;
  city: string;
};

export type GetMatchmakerCandidatesParams = {
  friend_sex: FriendSex;
  friend_age: number;
  city: string;
  age_from?: number;
  age_to?: number;
  cursor?: string;
};

export type GetMatchmakerCandidatesResponse = {
  results: User[];
  next_cursor: string | null;
};

export type PostMatchmakerSkipParams = {
  candidate_user_id: string;
};

export type PostMatchmakerSkipResponse = {
  remaining: number | null;
  reset_time: string | null;
};

export type PostMatchmakerRecommendationParams = {
  candidate_user_id: string;
  friend: MatchmakerFriend;
  note?: string | null;
};

export type PostMatchmakerRecommendationResponse = {
  invite_id: string;
  token: string;
  start_param: string;
  link: string;
  share_text: string;
  prepared_message_id: string | null;
  remaining: number | null;
};

export type MatchmakerRecommendationState = 'sent' | 'opened' | 'joined';

export type MatchmakerRecommendationCandidate = {
  id: string;
  username: string | null;
  age: number | null;
  photo: string | null;
};

export type MatchmakerRecommendation = {
  invite_id: string;
  created_at: string;
  state: MatchmakerRecommendationState;
  candidate: MatchmakerRecommendationCandidate | null;
};

export type GetMatchmakerRecommendationsParams = {
  cursor?: string;
};

export type GetMatchmakerRecommendationsResponse = {
  results: MatchmakerRecommendation[];
  next_cursor: string | null;
};

export type MatchmakerInviteOpenState =
  | 'register_first'
  | 'registered'
  | 'expired'
  | 'taken'
  | 'own_invite'
  | 'not_found';

export type PostMatchmakerInviteOpenParams = {
  token: string;
};

export type PostMatchmakerInviteOpenResponse = {
  state: MatchmakerInviteOpenState;
  matchmaker: { name: string | null } | null;
  note: string | null;
};

export type PostMatchmakerInviteCandidateParams = {
  token: string;
};

export type PostMatchmakerInviteCandidateResponse =
  | { state: 'available'; candidate: User }
  | { state: 'matched'; match_id: string }
  | { state: 'already_swiped'; action: 'like' | 'pass' }
  | { state: 'unavailable' };

export type MatchmakerInviteCandidateState =
  PostMatchmakerInviteCandidateResponse['state'];

export const getMatchmakerCandidates = (
  params: GetMatchmakerCandidatesParams,
) => {
  return fetcher<GetMatchmakerCandidatesResponse>({
    url: '/matchmaker/candidates',
    method: 'GET',
    params,
  });
};

export const postMatchmakerSkip = (data: PostMatchmakerSkipParams) => {
  return fetcher<PostMatchmakerSkipResponse>({
    url: '/matchmaker/skips',
    method: 'POST',
    data,
  });
};

export const postMatchmakerRecommendation = (
  data: PostMatchmakerRecommendationParams,
) => {
  return fetcher<PostMatchmakerRecommendationResponse>({
    url: '/matchmaker/recommendations',
    method: 'POST',
    data,
  });
};

export const getMatchmakerRecommendations = (
  params?: GetMatchmakerRecommendationsParams,
) => {
  return fetcher<GetMatchmakerRecommendationsResponse>({
    url: '/matchmaker/recommendations',
    method: 'GET',
    params,
  });
};

export const postMatchmakerInviteOpen = async (
  data: PostMatchmakerInviteOpenParams,
) => {
  const initData = await waitForTmaInitData();

  if (!initData) {
    throw new Error('Telegram initData is not available');
  }

  return fetcher<PostMatchmakerInviteOpenResponse>({
    url: '/matchmaker/invites/open',
    method: 'POST',
    data: {
      ...data,
      initData,
    },
    auth: null,
  });
};

export const postMatchmakerInviteCandidate = (
  data: PostMatchmakerInviteCandidateParams,
) => {
  return fetcher<PostMatchmakerInviteCandidateResponse>({
    url: '/matchmaker/invites/candidate',
    method: 'POST',
    data,
  });
};
